import FFmpegService from "@/services/media/FFmpegService";

export interface WaveformSegment {
    startTime: number;
    duration: number;
    peaks: number[];
    sampleRate: number;
}

export interface WaveformGeneratorOptions {
    sampleRate?: number;
    peaksPerSecond?: number;
}

export default class WaveformGenerator {
    private ffmpeg: FFmpegService;
    private loaded = false;
    private isRunning = false;
    private currentFile: File | null = null;
    private readonly inputDir = '/input';
    private readonly sampleRate: number;
    private readonly peaksPerSecond: number;

    constructor(options: WaveformGeneratorOptions = {}) {
        this.ffmpeg = new FFmpegService();
        this.sampleRate = options.sampleRate || 44100;
        this.peaksPerSecond = options.peaksPerSecond || 100;
    }

    public running(): boolean {
        return this.isRunning;
    }

    /**
     * Load and mount a file for processing
     */
    public async loadFile(file: File): Promise<void> {
        if (this.isRunning) {
            throw new Error('Cannot load file while generator is running');
        }

        // If it's a different file, unmount the previous one
        if (this.currentFile && (this.currentFile.name !== file.name || this.currentFile.size !== file.size)) {
            await this.unloadFile();
        }

        // If no file is currently loaded, load this one
        if (!this.currentFile) {
            await this.waitForLoad();

            try {
                await this.ffmpeg.createDir(this.inputDir);
                // @ts-ignore
                await this.ffmpeg.mount('WORKERFS', { files: [file] }, this.inputDir);
                this.currentFile = file;
            } catch (error) {
                // Clean up on mount failure
                try {
                    await this.ffmpeg.deleteDir(this.inputDir);
                } catch {
                    // Ignore cleanup errors
                }
                throw error;
            }
        }
    }

    /**
     * Unload the currently loaded file
     */
    public async unloadFile(): Promise<void> {
        if (this.isRunning) {
            throw new Error('Cannot unload file while generator is running');
        }
        await this.unmountCurrentFile();
    }

    /**
     * Check if a file is currently loaded
     */
    public isFileLoaded(): boolean {
        return this.currentFile !== null;
    }

    /**
     * Get the currently loaded file
     */
    public getCurrentFile(): File | null {
        return this.currentFile;
    }

    /**
     * Get the input file path for the currently loaded file
     */
    private getCurrentInputPath(): string {
        if (!this.currentFile) {
            throw new Error('No file is currently loaded. Call loadFile() first.');
        }
        return `${this.inputDir}/${this.currentFile.name}`;
    }

    /**
     * Unmount the currently mounted file
     */
    private async unmountCurrentFile(): Promise<void> {
        if (this.currentFile) {
            try {
                await this.ffmpeg.unmount(this.inputDir);
                await this.ffmpeg.deleteDir(this.inputDir);
            } catch {
                // Ignore cleanup errors
            }
            this.currentFile = null;
        }
    }

    /**
     * Generate waveform peaks for a specific time segment
     * Note: A file must be loaded first using loadFile()
     */
    public async generateSegment(
        startTime: number,
        duration: number,
        signal?: AbortSignal
    ): Promise<WaveformSegment> {
        if (this.isRunning) {
            throw new Error('WaveformGenerator is already processing another request');
        }

        if (!this.currentFile) {
            throw new Error('No file is currently loaded. Call loadFile() first.');
        }

        this.isRunning = true;

        // Generate unique output filename to avoid conflicts
        const outputFile = `/output_${Date.now()}_${Math.random().toString(36).substr(2, 9)}.wav`;

        try {
            // Get the input file path
            const inputFile = this.getCurrentInputPath();

            // Handle abort signal
            const abortHandler = () => {
                this.ffmpeg.terminate();
                this.loaded = false;
            };
            signal?.addEventListener('abort', abortHandler);

            // Generate WAV data for the specific time segment
            const command = [
                "-ss", startTime.toString(), // Move to BEFORE -i
                "-i", inputFile,
                "-vn",
                "-f", "wav",
                "-ar", this.sampleRate.toString(),
                "-ac", "1",
                "-t", duration.toString(),
                outputFile
            ];

            await this.ffmpeg.exec(command);

            // Read the WAV data
            const wavData = await this.ffmpeg.readFile(outputFile);
            const wavBuffer = (wavData as Uint8Array).buffer;

            // Extract PCM data from WAV file
            const pcmBuffer = this.extractPCMFromWAV(wavBuffer);

            // Calculate peaks from the PCM data
            const peaks = this.calculatePeaks(pcmBuffer, duration);

            // Only cleanup output file, keep input mounted
            await this.ffmpeg.deleteFile(outputFile);

            signal?.removeEventListener('abort', abortHandler);

            return {
                startTime,
                duration,
                peaks,
                sampleRate: this.sampleRate
            };
        } catch (error) {
            // Cleanup output file on error
            try {
                await this.ffmpeg.deleteFile(outputFile);
            } catch {
                // Ignore cleanup errors
            }

            if (signal?.aborted) {
                throw new DOMException('The operation was aborted.', 'AbortError');
            }
            throw error;
        } finally {
            this.isRunning = false;
        }
    }

    /**
     * Extract PCM data from WAV file buffer
     */
    private extractPCMFromWAV(wavBuffer: ArrayBuffer): Int16Array {
        const view = new DataView(wavBuffer);

        // Skip WAV header and find the data chunk
        // WAV format: RIFF header (12 bytes) + fmt chunk + data chunk
        let offset = 12; // Skip RIFF header

        // Find the data chunk
        while (offset < wavBuffer.byteLength - 8) {
            const chunkId = String.fromCharCode(
                view.getUint8(offset),
                view.getUint8(offset + 1),
                view.getUint8(offset + 2),
                view.getUint8(offset + 3)
            );
            const chunkSize = view.getUint32(offset + 4, true); // little endian

            if (chunkId === 'data') {
                // Found data chunk, extract PCM data
                const pcmStart = offset + 8;
                const pcmData = new ArrayBuffer(chunkSize);
                const pcmView = new Uint8Array(pcmData);
                const sourceView = new Uint8Array(wavBuffer, pcmStart, chunkSize);

                pcmView.set(sourceView);

                // Convert to Int16Array (assuming 16-bit PCM)
                return new Int16Array(pcmData);
            }

            // Skip to next chunk
            offset += 8 + chunkSize;
        }

        throw new Error('No data chunk found in WAV file');
    }

    /**
     * Calculate peaks from raw PCM data
     */
    private calculatePeaks(pcmBuffer: Int16Array, duration: number): number[] {
        const targetPeaks = Math.ceil(duration * this.peaksPerSecond);
        const samplesPerPeak = Math.floor(pcmBuffer.length / targetPeaks);
        const peaks: number[] = [];

        for (let i = 0; i < targetPeaks; i++) {
            const start = i * samplesPerPeak;
            const end = Math.min(start + samplesPerPeak, pcmBuffer.length);

            let min = 0;
            let max = 0;

            // Find min/max in this sample range
            for (let j = start; j < end; j++) {
                const sample = pcmBuffer[j] / 32768; // Normalize to -1 to 1
                min = Math.min(min, sample);
                max = Math.max(max, sample);
            }

            // Store the peak amplitude (max of absolute values)
            peaks.push(Math.max(Math.abs(min), Math.abs(max)));
        }

        return peaks;
    }

    private async waitForLoad(): Promise<void> {
        if (!this.loaded) {
            await this.ffmpeg.load();
            this.loaded = true;
        }
    }

    public async destroy(): Promise<void> {
        // Unload any currently loaded file
        await this.unloadFile();

        if (this.loaded) {
            this.ffmpeg.terminate();
            this.loaded = false;
        }
    }
}
